import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PaymentSuccess from "@/components/PaymentSuccess";
import { Loader2, XCircle } from "lucide-react";

// Handles Stripe redirect returns for all payment kinds. Polls the backend until
// the payment resolves, then shows the premium success animation and routes on.
export default function PaymentReturn() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const isMembership = location.pathname.includes("/membership/");
  const [state, setState] = useState("polling"); // polling | success | failed | timeout
  const [result, setResult] = useState({});
  const polled = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setResult(data);
          if (refresh) await refresh();
          setState("success");
          return;
        }
        if (data.status === "expired") { setState("failed"); return; }
      } catch { /* keep trying */ }
      polled.current += 1;
      if (polled.current >= 8) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  const kindVariant = () => {
    if (isMembership) return "access";
    if (result.kind === "marketplace_claim") return "claim";
    if (result.kind === "drone_deployment") return "drone";
    return "payment";
  };
  const continueTo = () => {
    if (result.kind === "marketplace_claim" && result.case_id) navigate(`/attorney?case=${result.case_id}`);
    else navigate("/attorney");
  };

  if (state === "polling") {
    return (
      <div className="min-h-screen bg-navy flex flex-col items-center justify-center text-white" data-testid="payment-polling">
        <Loader2 className="w-10 h-10 text-cyan-300 animate-spin" />
        <p className="mt-4 text-slate-300">Confirming your payment…</p>
      </div>
    );
  }
  if (state === "success") {
    return <PaymentSuccess variant={kindVariant()} amount={result.amount} onContinue={continueTo} />;
  }
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center" data-testid="payment-failed">
      <XCircle className="w-12 h-12 text-red-400" />
      <h1 className="mt-4 font-heading text-2xl font-bold text-navy">{state === "timeout" ? "Still processing…" : "Payment not completed"}</h1>
      <p className="mt-2 text-slate-500 max-w-md">{state === "timeout" ? "Your payment is taking longer than expected. It may still complete — check your portal shortly." : "The payment was cancelled or could not be completed. You can try again."}</p>
      <button onClick={() => navigate("/attorney")} className="mt-6 px-6 py-3 rounded-full bg-navy text-white font-semibold hover:bg-navy/90">Back to portal</button>
    </div>
  );
}
